import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { FaArrowLeft, FaPlay } from 'react-icons/fa';
import { IoShareOutline } from 'react-icons/io5';
import { AudioContext } from '../context/AudioContext';
import TrackItem from './TrackItem';
import '../styles/OtherUserAlbumDetail.css';

const OtherUserAlbumDetail = () => {
  const { id: userId, albumId } = useParams();
  const navigate = useNavigate();
  const { handleTrackClick, playAlbum } = useContext(AudioContext);
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();
  const currentUserUid = auth.currentUser?.uid;

  useEffect(() => {
    const fetchAlbumDetails = async () => {
      try {
        const albumResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads/album-details/${albumId}`
        );
        setAlbum(albumResponse.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching other user album:', error);
        setLoading(false);
      }
    };

    if (albumId) {
      fetchAlbumDetails();
    }
  }, [albumId]);

  const handleLikeTrack = async (trackId, trackUserId) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      window.alert('You must be logged in to like a track.');
      return;
    }

    if (trackUserId === currentUser.uid) {
      window.alert("You can't like your own track.");
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        'https://sync-api-troz.onrender.com/api/uploads/like',
        { trackId, firebaseUid: currentUser.uid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Track liked successfully!');
      setAlbum((prev) => ({
        ...prev,
        tracks: prev.tracks.map((track) =>
          track._id === trackId
            ? { ...track, likes: (track.likes || 0) + 1, likedBy: [...(track.likedBy || []), currentUser.uid] }
            : track
        ),
      }));
    } catch (error) {
      console.error('Error liking track:', error.response?.data || error.message);
      window.alert(
        error.response?.data?.message === 'You have already liked this track'
          ? 'You have already liked this track.'
          : 'An error occurred while liking the track.'
      );
    }
  };

  const handleStarTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    if (!firebaseUid) {
      window.alert('You must be logged in to star a track.');
      return;
    }

    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/star', {
        trackId,
        userId: firebaseUid,
      });
      window.alert('Track starred successfully!');
      setAlbum((prev) => ({
        ...prev,
        tracks: prev.tracks.map((track) =>
          track._id === trackId
            ? {
                ...track,
                stars: (track.stars || 0) + 1,
                starredBy: [...(track.starredBy || []), firebaseUid],
              }
            : track
        ),
      }));
    } catch (error) {
      console.error('Error starring track:', error.response?.data || error.message);
      window.alert(
        error.response?.data?.message === 'You have already starred a track from this artist'
          ? 'You already starred a track from this user.'
          : 'An error occurred while starring the track.'
      );
    }
  };

  const handlePlayAlbum = () => {
    if (album && album.tracks.length > 0) {
      playAlbum(albumId);
    } else {
      window.alert('No tracks available to play in this album.');
    }
  };

  const handleShareAlbum = async () => {
    if (!albumId || !album) {
      window.alert('No album available to share.');
      return;
    }

    const firebaseUid = album.tracks?.[0]?.user?.firebaseUid || userId;
    if (!firebaseUid) {
      window.alert('Unable to determine album owner.');
      return;
    }

    const deepLink = `https://syncmusic.app/otheruserdiscography/${firebaseUid}/album/${albumId}`;
    const message = `🎵 Check out this album: "${album.albumTitle}" by ${album.artistName}\n\n🔥 Listen now: ${deepLink}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: `Share ${album.albumTitle}`,
          text: message,
          url: deepLink,
        });
      } else {
        await navigator.clipboard.writeText(message);
        window.alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing album:', error);
      window.alert('Failed to share album.');
    }
  };

  if (loading) {
    return (
      <div className="other-user-album-detail-loading">
        <div className="loader"></div>
        <p>Loading album...</p>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="other-user-album-detail-loading">
        <p>Album not found.</p>
      </div>
    );
  }

  return (
    <div className="other-user-album-detail-page">
      <div className="other-user-album-detail-container">
        <div className="other-user-album-detail-banner">
          {album.coverPhoto ? (
            <img
              src={album.coverPhoto}
              alt="Album Cover"
              className="other-user-album-detail-banner-image"
            />
          ) : (
            <div className="other-user-album-detail-banner-placeholder" />
          )}
          <p className="other-user-album-detail-album-title">{album.albumTitle}</p>
        </div>

        <div className="other-user-album-detail-header">
          <button
            onClick={() => navigate(-1)}
            className="other-user-album-detail-back-button"
          >
            <FaArrowLeft size={24} />
          </button>
          <p className="other-user-album-detail-header-text">{album.artistName}</p>
          <button
            onClick={handlePlayAlbum}
            className="other-user-album-detail-play-button"
          >
            <FaPlay size={18} />
          </button>
          <button
            onClick={handleShareAlbum}
            className="other-user-album-detail-share-button"
          >
            <IoShareOutline size={24} />
          </button>
        </div>

        <div className="other-user-album-detail-results">
          {album.tracks && album.tracks.length > 0 ? (
            album.tracks.map((track) => (
              <TrackItem
                key={track._id}
                track={track}
                onPlay={() => handleTrackClick(track)}
                onLike={() => handleLikeTrack(track._id, track.user?.firebaseUid)}
                onStar={() => handleStarTrack(track._id)}
                currentUserId={currentUserUid}
                isOwner={false}
                showPinButton={false}
                showDeleteButton={false}
              />
            ))
          ) : (
            <p className="other-user-album-detail-no-results">No tracks in this album.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OtherUserAlbumDetail;